import { createSlice } from "@reduxjs/toolkit";
import {
    startTimer,
    stopTimer,
    getActiveTimer,
    getTimeEntries,
    getTimeSummary,
    updateTimeEntry,
    deleteTimeEntry,
    createTimesheet,
    getTimesheets,
    getTimesheetById,
    submitTimesheet,
    approveTimesheet,
    rejectTimesheet
} from './thunk';

export const initialState = {
    // Time Tracking state
    activeTimer: null,
    timeEntries: [],
    timeSummary: null,
    loading: false,
    error: null,
    
    // Timesheet state
    timesheets: [],
    currentTimesheet: null,
    timesheetLoading: false,
    timesheetError: null
};

const TimeTrackingSlice = createSlice({
    name: 'TimeTracking',
    initialState,
    reducers: {
        clearActiveTimer: (state: any) => {
            state.activeTimer = null;
        },
        clearTimeEntries: (state: any) => {
            state.timeEntries = [];
        },
        clearTimeSummary: (state: any) => {
            state.timeSummary = null;
        },
        clearCurrentTimesheet: (state: any) => {
            state.currentTimesheet = null;
        }
    },
    extraReducers: (builder) => {
        // Start Timer
        builder.addCase(startTimer.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(startTimer.fulfilled, (state: any, action: any) => {
            state.activeTimer = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(startTimer.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Stop Timer
        builder.addCase(stopTimer.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(stopTimer.fulfilled, (state: any) => {
            state.activeTimer = null;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(stopTimer.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Get Active Timer
        builder.addCase(getActiveTimer.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(getActiveTimer.fulfilled, (state: any, action: any) => {
            state.activeTimer = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getActiveTimer.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
            state.activeTimer = null;
        });

        // Get Time Entries
        builder.addCase(getTimeEntries.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(getTimeEntries.fulfilled, (state: any, action: any) => {
            state.timeEntries = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getTimeEntries.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Get Time Summary
        builder.addCase(getTimeSummary.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(getTimeSummary.fulfilled, (state: any, action: any) => {
            state.timeSummary = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getTimeSummary.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Update Time Entry
        builder.addCase(updateTimeEntry.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(updateTimeEntry.fulfilled, (state: any, action: any) => {
            // Update the entry in the list
            const index = state.timeEntries.findIndex((entry: any) => entry.id === action.payload.id);
            if (index !== -1) {
                state.timeEntries[index] = action.payload;
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateTimeEntry.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Delete Time Entry
        builder.addCase(deleteTimeEntry.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(deleteTimeEntry.fulfilled, (state: any, action: any) => {
            state.timeEntries = state.timeEntries.filter((entry: any) => entry.id !== action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(deleteTimeEntry.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
            state.loading = false;
        });

        // Create Timesheet
        builder.addCase(createTimesheet.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(createTimesheet.fulfilled, (state: any, action: any) => {
            state.timesheets.unshift(action.payload);
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(createTimesheet.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });

        // Get Timesheets
        builder.addCase(getTimesheets.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(getTimesheets.fulfilled, (state: any, action: any) => {
            state.timesheets = action.payload;
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(getTimesheets.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });

        // Get Timesheet By Id
        builder.addCase(getTimesheetById.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(getTimesheetById.fulfilled, (state: any, action: any) => {
            state.currentTimesheet = action.payload;
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(getTimesheetById.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });

        // Submit Timesheet
        builder.addCase(submitTimesheet.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(submitTimesheet.fulfilled, (state: any, action: any) => {
            // Update the timesheet in the list
            const index = state.timesheets.findIndex((ts: any) => ts.id === action.payload.id);
            if (index !== -1) {
                state.timesheets[index] = action.payload;
            }
            state.currentTimesheet = action.payload;
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(submitTimesheet.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });

        // Approve Timesheet
        builder.addCase(approveTimesheet.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(approveTimesheet.fulfilled, (state: any, action: any) => {
            // Update the timesheet in the list
            const index = state.timesheets.findIndex((ts: any) => ts.id === action.payload.id);
            if (index !== -1) {
                state.timesheets[index] = action.payload;
            }
            state.currentTimesheet = action.payload;
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(approveTimesheet.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });

        // Reject Timesheet
        builder.addCase(rejectTimesheet.pending, (state: any) => {
            state.timesheetLoading = true;
        });
        builder.addCase(rejectTimesheet.fulfilled, (state: any, action: any) => {
            // Update the timesheet in the list
            const index = state.timesheets.findIndex((ts: any) => ts.id === action.payload.id);
            if (index !== -1) {
                state.timesheets[index] = action.payload;
            }
            state.currentTimesheet = action.payload;
            state.timesheetLoading = false;
            state.timesheetError = null;
        });
        builder.addCase(rejectTimesheet.rejected, (state: any, action: any) => {
            state.timesheetError = action.payload?.error || null;
            state.timesheetLoading = false;
        });
    }
});

export const { clearActiveTimer, clearTimeEntries, clearTimeSummary, clearCurrentTimesheet } = TimeTrackingSlice.actions;
export default TimeTrackingSlice.reducer;

